import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldQuestion, ShieldCheck, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

// Anti-impersonation gate: the user must gain a little XP in a random skill on the
// account, then verify — proving they actually control it before it can earn points.
export default function OwnershipChallenge({ profile, onVerified }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ch = profile.ownership_challenge || {};
  const skill = ch.skill || "a skill";
  const isTotal = ch.metric === "total_xp";

  const verify = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/profiles/${profile.id}/verify-ownership`);
      toast.success(data.message || "Ownership verified!");
      onVerified?.(data.profile);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="osrs-panel p-6 border-2 border-[#8a6d1f]" data-testid="ownership-challenge">
      <div className="flex items-center gap-3 mb-4">
        <div className="skill-node p-2 shrink-0">
          <ShieldQuestion size={26} className="text-[#FFCF3F]" style={{ filter: "drop-shadow(1px 1px 0 #000)" }} />
        </div>
        <div>
          <h2 className="osrs-title text-xl leading-tight">Prove you own this account</h2>
          <p className="osrs-num text-[#00FFFF] osrs-shadow text-sm">{profile.username}</p>
        </div>
      </div>

      <p className="osrs-shadow text-[#E6A519] text-sm mb-5 leading-snug">
        To stop people from adding accounts that aren't theirs, verify control before <span className="text-[#FFCF3F]">{profile.username}</span> can earn points or join the bond race.
      </p>

      <div className="osrs-panel-parchment parchment-tex p-4 mb-5">
        <p className="text-[#2a2113] font-bold text-lg leading-snug flex items-center gap-2">
          <Sparkles size={18} /> Your challenge
        </p>
        <ol className="text-[#4a3c22] text-sm mt-2 leading-relaxed list-decimal list-inside space-y-1">
          <li>Log into <span className="font-bold">{profile.username}</span> in Old School RuneScape.</li>
          <li>
            {isTotal ? (
              <>Gain a little XP in <span className="font-bold">any skill</span> (a few actions is plenty).</>
            ) : (
              <>Gain a little <span className="font-bold" data-testid="challenge-skill">{skill}</span> XP — just a few actions.</>
            )}
          </li>
          <li>Wait ~a minute for the official hiscores to update, then click Verify.</li>
        </ol>
      </div>

      <button
        data-testid="verify-ownership-btn"
        onClick={verify}
        disabled={busy}
        className="osrs-btn osrs-btn-green px-6 py-3 text-sm flex items-center gap-2"
      >
        <ShieldCheck size={16} /> {busy ? "Checking hiscores..." : "Verify ownership"}
      </button>

      {error && (
        <div className="mt-4 px-4 py-3 border-2 border-[#a33] bg-[#3a1414] text-[#FF9A9A] osrs-shadow text-sm leading-snug flex items-start gap-2" data-testid="ownership-error">
          <ShieldQuestion size={16} className="shrink-0 mt-0.5 text-[#FF7A7A]" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
